// StoryMessage.js
'use client';
import { connect } from 'react-redux';
import { replace, goBack } from 'redux-first-history';
import { withTranslation } from 'react-i18next';

import { FEATURE_STORY_VOLUME_BUTTON } from '../RemoteConfigKeys.js';
import withRouter from '../component/WithRouter.jsx';
import StoryMessage from '../page/StoryMessage.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getAssetData from '../selector/getAssetData.js';
import getOperationData from '../selector/getOperationData.js';
import getMessageData from '../selector/getMessageData.js';
import getMessageList from '../selector/getMessageList.js';
import fetchMessagesByUsingList from '../action/fetchMessagesByUsingList.js';
import getUserData from '../selector/getUserData.js';
import fetchDrmLicenseAvailability from '../action/fetchDrmLicenseAvailability.js';
import { getKeySystem } from '../resource/getDrmInfo.js';
import getIsMatchedPathname from '../resource/getIsMatchedPathname.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import fetchUser from '../action/fetchUser.js';
import getNetworkingSummary from '../selector/getNetworkingSummary.js';
import getMessageThumbnail from '../selector/getMessageThumbnail.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getModalData from '../selector/getModalData.js';
import getMeData from '../selector/getMeData.js';
import getMessageDataWithVariable, {
  VariableName,
} from '../selector/getMessageDataWithVariable.js';
import getStoryPreviewIds from '../selector/getStoryPreviewIds.js';
import fetchUserIsBlocked from '../action/fetchUserIsBlocked.js';
import setOperationChatroomData from '../action/setOperationChatroomData.js';
import fetchChatroomMessages from '../action/fetchChatroomMessages.js';
import setClickedShareButton from '../action/setClickedShareButton.js';

const mapStateToProps = (
  state,
  { messageId: messageIdArg, match, location }
) => {
  const messageId = messageIdArg || match?.params?.messageId;
  const listPath = location.state?.listPath;
  const usingList = listPath || [];
  const listType = usingList[0];
  const itemIds = getMessageList(state, listType, usingList);
  const senderId = getMessageData(state, messageId, 'senderId');
  const fetchMessageDetailStatus = getNetworkingSummary(state, [
    'messages',
    messageId,
    'detail',
  ]);
  const nextPage = getMessageList(state, listType, usingList, 'nextPage');
  const isNextPageFetching = !!getNetworkingData(
    state,
    [...usingList, nextPage],
    'isFetching'
  );
  const meId = getMeData(state, 'id');
  const assetIds = getMessageData(state, messageId, 'assetIds');
  const assetId = assetIds?.[0];
  const mediaType = assetId
    ? getAssetData(state, [assetId], 'contentType')
    : getMessageData(state, messageId, 'mediaType');
  const isVideo = mediaType?.startsWith('video');
  const canViewMedia = getMessageData(state, messageId, 'canViewMedia');
  const ogMessageThumbnail = getMessageThumbnail(
    state,
    messageId,
    '',
    '512x512',
    'jpg',
    isVideo || canViewMedia ? 'clear' : 'blurred'
  );
  const meUsername = getMeData(state, 'username');
  return {
    messageId,
    location,
    drmTestStatus: getOperationData(state, ['drm'], 'testStatus'),
    badges: getMessageData(state, messageId, 'badges'),
    itemIds,
    storyPreviewIds: getStoryPreviewIds(state),
    usingList,
    listType,
    senderId,
    currentIndex: itemIds?.length ? itemIds.indexOf(messageId) : 0,
    deviceWidth: getOperationData(state, ['device'], 'width'),
    mediaType,
    type: getMessageData(state, messageId, 'type'),
    isPlayerMuted: getOperationData(state, ['player'], 'isMuted'),
    caption: getMessageDataWithVariable(
      state,
      messageId,
      'captionText',
      `${VariableName.USERNAME}:${meUsername}`
    ),
    totalCount: getMessageList(state, listType, usingList, 'totalCount'),
    nextPage,
    senderName: getUserData(state, senderId, 'username'),
    ogMessageThumbnail,
    isUnlocked: canViewMedia,
    fetchMessageDetailError: fetchMessageDetailStatus?.error,
    isNextPageFetching,
    isInputFocused: getOperationData(
      state,
      ['message', messageId],
      'isInputFocused'
    ),
    isMessageDetailGeneralModalOpen:
      'display' === getModalData(state, 'MessageMoreModal', 'status'),
    isMessagePackUpsellingModalOpen:
      'display' === getModalData(state, 'MessagePackUpsellingModal', 'status'),
    isAuthed: !!meId,
    isOnPostDetail: getIsMatchedPathname({
      pathname: location?.pathname,
      dataKey: 'isOnPostDetail',
    }),
    isVolumeButtonEnabled:
      getRemoteConfigData(state, FEATURE_STORY_VOLUME_BUTTON) === 1,
    assetIds,
    isClikedShareButton: getOperationData(
      state,
      ['message', messageId],
      'isClikedShareButton'
    ),
    isBroadcastListExpanded:
      getOperationData(state, ['message', messageId], 'expandedField') ===
      'broadcastList',
    shouldFetchMessageTranslation: true, // For withFetchTranslation
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchUserIsBlocked: ({ userId }) =>
      dispatch(fetchUserIsBlocked({ userId })),
    fetchUser: ({ id, httpProxyHeaders }) =>
      dispatch(fetchUser({ id, httpProxyHeaders })),
    fetchMessageDetail: ({ id, httpProxyHeaders }) =>
      dispatch(fetchMessageDetail({ id, httpProxyHeaders })),
    fetchViewScope: ({ messageId }) =>
      dispatch(
        fetchDrmLicenseAvailability({ messageId, keySystem: getKeySystem() })
      ),
    goBack: () => dispatch(goBack()),
    replace: (...args) => dispatch(replace(...args)),
    fetchMessagesByUsingList: data => dispatch(fetchMessagesByUsingList(data)),
    setOperationChatroomData: ({ key, payload }) =>
      dispatch(setOperationChatroomData({ key, payload })),
    fetchChatroomMediaMessages: ({ id }) =>
      dispatch(fetchChatroomMessages({ id, isFetchMediaOnly: true })),
    setClickedShareButton: ({ messageId, clicked }) =>
      dispatch(setClickedShareButton({ messageId, clicked })),
  };
};

export default withRouter(
  withTranslation()(connect(mapStateToProps, mapDispatchToProps)(StoryMessage))
);
